"use client";

import { Avatar, Menu } from "@mantine/core";
import Link from "next/link";
import { useTranslations } from "next-intl";

import { useAppContext } from "../../context";
import { Icon } from "../../icons";
import type { UserMenuItem } from "../../types";
import { cn } from "../../utils";
import { titleCase } from "../../utils/string";

type UserDropdownProps = {
  locale: string;
  menuItems?: UserMenuItem[];
  /** Opens the app's subscription management modal (e.g. via @mantine/modals) */
  onManageSubscription?: () => void;
};

const defaultMenuItems: UserMenuItem[] = [
  { icon: "user", labelKey: "user-menu.my-profile", href: "/profile" },
  { icon: "signOut", labelKey: "user-menu.log-out", danger: true },
];

export function UserDropdown({
  locale,
  menuItems,
  onManageSubscription,
}: UserDropdownProps) {
  const t = useTranslations();
  const { userInfo, onLogout, subscriptionsInfo } = useAppContext();
  const items = menuItems ?? defaultMenuItems;

  const fullName = titleCase(
    `${userInfo.firstName} ${userInfo.lastName}`.trim(),
  );
  const hasSubscriptions = !!subscriptionsInfo?.runningSubscriptions?.length;

  return (
    <Menu
      shadow="lg"
      width={220}
      radius="lg"
      position="bottom-end"
      id="user-dropdown-menu"
    >
      <Menu.Target>
        <button
          data-testid="user-menu-trigger"
          className="flex items-center gap-2 rounded-xl px-2 py-1.5 transition-all duration-shell ease-shell hover:bg-surface-hover"
        >
          <Avatar
            src={userInfo.avatarUrl}
            alt={fullName}
            size="md"
            name={fullName}
            radius="xl"
          />
          <span className="hidden text-base font-medium text-text-secondary lg:block">
            {fullName || "..."}
          </span>
        </button>
      </Menu.Target>

      <Menu.Dropdown data-testid="user-menu" className="border-0 p-2">
        <div className="px-3 py-2">
          <p className="text-base font-semibold text-text-primary">
            {fullName}
          </p>
        </div>
        <Menu.Divider />
        {items.map((item, idx) => {
          const showDivider = item.danger && idx > 0;
          const iconSection = (
            <span
              className={
                item.danger ? "text-text-danger" : "text-text-secondary"
              }
            >
              <Icon name={item.icon} size={16} />
            </span>
          );
          const itemClass = cn(
            "rounded-lg text-base transition-colors duration-shell",
            item.danger ? "text-text-danger" : "text-text-secondary",
          );
          // From the i18n key, not the label: `user-menu.log-out` → `user-menu-log-out`.
          const testId = `user-menu-${item.labelKey.split(".").pop()}`;

          return (
            <div key={item.labelKey}>
              {showDivider && hasSubscriptions && onManageSubscription && (
                <Menu.Item
                  onClick={onManageSubscription}
                  data-testid="user-menu-my-subscription"
                  leftSection={
                    <span className="text-text-secondary">
                      <Icon name="settings" size={16} />
                    </span>
                  }
                  className="rounded-lg text-base text-text-secondary transition-colors duration-shell"
                >
                  {t("user-menu.my-subscription")}
                </Menu.Item>
              )}
              {showDivider && <Menu.Divider />}
              {item.href ? (
                <Menu.Item
                  component={Link}
                  href={`/${locale}${item.href}`}
                  leftSection={iconSection}
                  className={itemClass}
                  data-testid={testId}
                >
                  {t(item.labelKey)}
                </Menu.Item>
              ) : (
                <Menu.Item
                  onClick={item.onClick ?? (item.danger ? onLogout : undefined)}
                  leftSection={iconSection}
                  className={itemClass}
                  data-testid={testId}
                >
                  {t(item.labelKey)}
                </Menu.Item>
              )}
            </div>
          );
        })}
      </Menu.Dropdown>
    </Menu>
  );
}
